import React from 'react';
import { Button, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatList } from '../../../../i18n';
import type { PlanningSelectionInfo, PlanningActionType } from '../../../../types/planManagement';
import {
  GATE_CELLS_ERROR_MESSAGES,
  isFenceSubtype,
  isGateAction,
  isShadeStructureAction,
  SHADE_STRUCTURE_ACTIONS,
  SHADE_STRUCTURE_PRESETS,
  validateGateCells,
} from '../planningStructuralObjects';
import { isFourConnectedCellGroup } from '../../../../utils/planCells';

const { Text } = Typography;
type PlanningPlacementTool = Exclude<PlanningActionType, 'delete'>;

const selectedCells = (selection: PlanningSelectionInfo) => Array.from(selection.selectedCellKeys).map((key) => {
  const [row, col] = key.split(':').map(Number);
  return { row, col };
});

interface SelectionRect {
  rows: number;
  cols: number;
  exceedsBoundary?: boolean;
}

interface PlanningModeContentProps {
  allowBedSelection: boolean;
  isBusy: boolean;
  planningSelection: PlanningSelectionInfo | null;
  selectionRect: SelectionRect | null;
  onPlanningAction: (action: PlanningActionType) => void;
  onCancelPlanningSelection: () => void;
}

const PlanningModeContent: React.FC<PlanningModeContentProps> = ({
  allowBedSelection,
  isBusy,
  planningSelection,
  selectionRect,
  onPlanningAction,
  onCancelPlanningSelection,
}) => {
  const { t } = useTranslation('planning');
  const [selectedTool, setSelectedTool] = React.useState<PlanningPlacementTool>('create-bed');

  if (!allowBedSelection) {
    return <Text type="secondary">{t('planningMode.clickPlot')}</Text>;
  }

  if (!planningSelection && !selectionRect) {
    return (
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Text type="secondary">{t('planningMode.steps')}</Text>
        <Space wrap>
          <Button type={selectedTool === 'create-bed' ? 'primary' : 'default'} onClick={() => setSelectedTool('create-bed')} disabled={isBusy}>{t('planningMode.tools.bed')}</Button>
          <Button type={selectedTool === 'fill-path' ? 'primary' : 'default'} onClick={() => setSelectedTool('fill-path')} disabled={isBusy}>{t('planningMode.tools.path')}</Button>
          <Button type={selectedTool === 'greenhouse' ? 'primary' : 'default'} onClick={() => setSelectedTool('greenhouse')} disabled={isBusy}>{t('planningMode.tools.greenhouse')}</Button>
          {SHADE_STRUCTURE_ACTIONS.map((action) => {
            const preset = SHADE_STRUCTURE_PRESETS[action];
            return (
              <Button
                key={action}
                type={selectedTool === action ? 'primary' : 'default'}
                onClick={() => setSelectedTool(action)}
                disabled={isBusy}
                title={preset.hint}
              >
                {preset.label}
              </Button>
            );
          })}
        </Space>
      </Space>
    );
  }

  if (selectionRect && !planningSelection) {
    return <Text type="secondary">{t('planningMode.selectionRect', { rows: selectionRect.rows, cols: selectionRect.cols, count: selectionRect.rows * selectionRect.cols })}</Text>;
  }

  if (!planningSelection) return null;

  const parts: string[] = [];
  if (planningSelection.emptyCellCount > 0) parts.push(t('planningMode.parts.empty', { count: planningSelection.emptyCellCount }));
  if (planningSelection.beds.length > 0) parts.push(t('planningMode.parts.beds', { count: planningSelection.beds.length }));
  if (planningSelection.pathCellCount > 0) parts.push(t('planningMode.parts.paths', { count: planningSelection.pathCellCount }));
  if (planningSelection.obstacleCellCount > 0) parts.push(t('planningMode.parts.obstacles', { count: planningSelection.obstacleCellCount }));
  if (planningSelection.structuralObjectIds.length > 0) parts.push(t('planningMode.parts.objects', { count: planningSelection.structuralObjectIds.length }));
  const summary = t('planningMode.summary', { count: planningSelection.totalCells, parts: formatList(parts, { type: 'unit' }) });

  const hasBeds = planningSelection.beds.length > 0;
  const hasPaths = planningSelection.pathCellCount > 0;
  const hasObstacles = planningSelection.obstacleCellCount > 0;
  const hasStructuralObjects = planningSelection.structuralObjectIds.length > 0;
  // Забор, изгородь, постройка, фонарь: грядку и дорожку поверх них не положить
  // (теплица — покрытие, под ней можно).
  const hasNonCoveringStructures = planningSelection.nonCoveringStructuralObjectCellCount > 0;
  const hasStructuralObjectBoundaryCrossing = planningSelection.structuralObjectBoundaryCrossingCellCount > 0;

  const toolValidation = (() => {
    if (selectionRect?.exceedsBoundary) {
      return { valid: false, message: t('planningMode.errors.outOfBounds') };
    }

    if (selectedTool === 'create-bed') {
      if (hasBeds) return { valid: false, message: t('planningMode.errors.onlyGreenhouseOverBeds') };
      if (hasObstacles) return { valid: false, message: t('planningMode.errors.overObstacle') };
      if (hasNonCoveringStructures) return { valid: false, message: t('planningMode.errors.bedOverStructure') };
      if (hasStructuralObjectBoundaryCrossing) return { valid: false, message: t('planningMode.errors.bedCrossesBoundary') };
      return { valid: true, message: hasPaths ? t('planningMode.pathsReplacedByBed') : null };
    }

    if (selectedTool === 'fill-path') {
      if (hasBeds) return { valid: false, message: t('planningMode.errors.onlyGreenhouseOverBeds') };
      if (hasObstacles) return { valid: false, message: t('planningMode.errors.overObstacle') };
      if (hasNonCoveringStructures) return { valid: false, message: t('planningMode.errors.pathOverStructure') };
      if (hasStructuralObjectBoundaryCrossing) return { valid: false, message: t('planningMode.errors.pathCrossesBoundary') };
      return { valid: true, message: null };
    }

    if (selectedTool === 'greenhouse') {
      if (hasObstacles) return { valid: false, message: t('planningMode.errors.greenhouseOverObstacle') };
      if (hasStructuralObjects) return { valid: false, message: t('planningMode.errors.greenhouseOverObject') };
      const coveredBeds = planningSelection.beds.length;
      const coveredPaths = planningSelection.pathCellCount;
      const covered = [
        coveredBeds > 0 && t('planningMode.parts.beds', { count: coveredBeds }),
        coveredPaths > 0 && t('planningMode.parts.paths', { count: coveredPaths }),
      ].filter((item): item is string => Boolean(item));
      return {
        valid: true,
        message: covered.length
          ? t('planningMode.greenhouseCovers', { items: formatList(covered) })
          : t('planningMode.greenhouseCoversSelection'),
      };
    }

    if (isGateAction(selectedTool)) {
      const preset = SHADE_STRUCTURE_PRESETS[selectedTool];
      if (hasBeds) return { valid: false, message: t('planningMode.errors.objectOverBed') };
      if (hasObstacles) return { valid: false, message: t('planningMode.errors.objectOverObstacle') };
      // Клетки чужого объекта допустимы только у забора/изгороди — калитка врезается в него.
      const nonFenceObjects = planningSelection.structuralObjectIds
        .filter((id) => !planningSelection.fenceStructuralObjectIds.includes(id));
      if (nonFenceObjects.length > 0) return { valid: false, message: t('planningMode.errors.selectionHasObject') };
      const error = validateGateCells(selectedCells(planningSelection), preset.gateLeaves ?? 1);
      if (error) return { valid: false, message: GATE_CELLS_ERROR_MESSAGES[error] };
      if (hasStructuralObjects) return { valid: true, message: t('planningMode.gateCutsFence', { label: preset.label }) };
      return {
        valid: true,
        message: hasPaths
          ? t('planningMode.overPath', { label: preset.label })
          : preset.hint,
      };
    }

    if (isShadeStructureAction(selectedTool)) {
      const preset = SHADE_STRUCTURE_PRESETS[selectedTool];
      if (hasBeds) return { valid: false, message: t('planningMode.errors.objectOverBed') };
      if (hasObstacles) return { valid: false, message: t('planningMode.errors.objectOverObstacle') };
      if (hasStructuralObjects) return { valid: false, message: t('planningMode.errors.selectionHasObject') };
      if (preset.singleCell && planningSelection.totalCells !== 1) {
        return { valid: false, message: t('planningMode.errors.singleCell', { label: preset.label }) };
      }
      if (isFenceSubtype(preset.subtype) && !isFourConnectedCellGroup(selectedCells(planningSelection))) {
        return { valid: false, message: t('planningMode.errors.connectedLine', { label: preset.label }) };
      }
      return {
        valid: true,
        message: hasPaths
          ? t('planningMode.overPath', { label: preset.label })
          : preset.hint,
      };
    }

    return { valid: false, message: t('planningMode.errors.toolUnavailable') };
  })();

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Text>{summary}</Text>

      <Space wrap>
        <Button type={selectedTool === 'create-bed' ? 'primary' : 'default'} onClick={() => setSelectedTool('create-bed')} disabled={isBusy}>{t('planningMode.tools.bed')}</Button>
        <Button type={selectedTool === 'fill-path' ? 'primary' : 'default'} onClick={() => setSelectedTool('fill-path')} disabled={isBusy}>{t('planningMode.tools.path')}</Button>
        <Button type={selectedTool === 'greenhouse' ? 'primary' : 'default'} onClick={() => setSelectedTool('greenhouse')} disabled={isBusy}>{t('planningMode.tools.greenhouse')}</Button>
        {SHADE_STRUCTURE_ACTIONS.map((action) => {
          const preset = SHADE_STRUCTURE_PRESETS[action];
          return (
            <Button
              key={action}
              type={selectedTool === action ? 'primary' : 'default'}
              onClick={() => setSelectedTool(action)}
              disabled={isBusy}
              title={preset.hint}
            >
              {preset.label}
            </Button>
          );
        })}
      </Space>

      {toolValidation.message && (
        <Text type={toolValidation.valid ? 'secondary' : 'danger'}>{toolValidation.message}</Text>
      )}

      <Space wrap>
        <Button
          type="primary"
          disabled={isBusy || !toolValidation.valid}
          onClick={() => onPlanningAction(selectedTool)}
        >
          {t('planningMode.confirm')}
        </Button>
        <Button danger icon={<DeleteOutlined />} onClick={() => onPlanningAction('delete')} disabled={isBusy}>
          {t('planningMode.deleteSelection')}
        </Button>
        <Button onClick={onCancelPlanningSelection} disabled={isBusy}>{t('planningMode.resetSelection')}</Button>
      </Space>
    </Space>
  );
};

export default PlanningModeContent;
